import React, { useState, useEffect } from "react";
import sparklesSmallTokens from "@amzn/meridian-tokens/base/icon/sparkles-small";
import starFilledTokens from "@amzn/meridian-tokens/base/icon/star-filled";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Button, Divider, Expander, Icon } from "@amzn/meridian";
import Loader from "@amzn/meridian/loader";
import { WasteProfilePathway, ClassifyWasteProfileResponse } from "src/types/classificationTypes";
import { classifyWasteProfile } from "src/services/classificationService";
import { useAppSelector } from "src/store/store";
import "./classification.scss";

function RemainingProfilesExpander({ remainingProfileIds }: { remainingProfileIds: string[] }) {
  const [expanded, setExpanded] = useState(false);
  if (remainingProfileIds.length === 0) return null;
  return (
    <>
      <Divider />
      <Expander title="View more profiles" type="inline" open={expanded} onChange={setExpanded}>
        <Column spacing="300" spacingInset="none">
          {remainingProfileIds.map((id, idx) => (
            <React.Fragment key={idx}>
              <Text type="h100">{id}</Text>
              {idx < remainingProfileIds.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Column>
      </Expander>
    </>
  );
}

function ReasoningExpander({ reasoning }: { reasoning: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <Divider />
      <Expander title="Why these profiles?" type="inline" open={expanded} onChange={setExpanded}>
        <Text type="b200" color="secondary">
          {reasoning}
        </Text>
      </Expander>
    </>
  );
}

// ---- Main component ----

interface WasteProfileCardProps {
  asin: string;
  itemName?: string;
}

function WasteProfileCard({ asin, itemName }: WasteProfileCardProps) {
  const { activeSiteCode } = useAppSelector((state) => state.user);
  const [wasteProfileData, setWasteProfileData] = useState<ClassifyWasteProfileResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchClassification = async () => {
      setIsLoading(true);
      setWasteProfileData(null);
      try {
        const res = await classifyWasteProfile({
          itemName: itemName ?? "",
          asin,
          siteName: activeSiteCode,
        });
        if (!cancelled) setWasteProfileData(res);
      } catch (error) {
        console.error("[WasteProfileCard] Failed to classify waste profile:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchClassification();
    return () => {
      cancelled = true;
    };
  }, [asin, itemName, activeSiteCode, retryCount]);

  if (isLoading) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Row spacing="300" alignmentVertical="center">
          <Loader size="small" type="circular" />
          <Text type="b300" color="secondary">
            Loading waste profile...
          </Text>
        </Row>
      </Column>
    );
  }

  if (!wasteProfileData) {
    return (
      <Column className="home-card" spacingInset="400" spacing="300">
        <Text type="b300" color="error">
          Unable to load waste profile. Please try again.
        </Text>
        <Button onClick={() => setRetryCount((c) => c + 1)} size="small" type="secondary" >
          Retry
        </Button>
      </Column>
    );
  }

  const { recommendedWasteProfilesIds, allWasteProfilesIds, pathway, reasoning } = wasteProfileData;

  const recommendedSet = new Set(recommendedWasteProfilesIds);
  const remainingProfiles = allWasteProfilesIds.filter((id) => !recommendedSet.has(id));

  // Golden ASIN — all profiles shown
  if (pathway === WasteProfilePathway.GOLDEN_ASIN) {
    return (
      <Column className="home-card asin-waste-profile--golden" spacingInset="400 none none">
        <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
          <Icon tokens={starFilledTokens} />
          <Text type="h200" color="primary">
            Golden ASIN Profile
          </Text>
        </Row>
        <Column className="home-card" spacingInset="400" spacing="200">
          {allWasteProfilesIds.map((profileId, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Text type="h200">{profileId}</Text>
            </React.Fragment>
          ))}
          {reasoning && <ReasoningExpander reasoning={reasoning} />}
        </Column>
      </Column>
    );
  }

  // AI-recommended (SIMILARITY, SDS_EXTRACTION, AI_EXTERNAL_RESEARCH)
  return (
    <Column className="home-card asin-waste-profile" spacingInset="400 none none">
      <Row alignmentVertical="center" spacing="300" spacingInset="none 500">
        <Icon color="inverted" tokens={sparklesSmallTokens} />
        <Text type="h200" color="inverted">
          Profile Recommended by AI
        </Text>
      </Row>
      <Column className="home-card" spacingInset="none">
        <Column spacingInset="400 500" spacing="300">
          {recommendedWasteProfilesIds.map((profileId, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <Divider />}
              <Text type="h200">{profileId}</Text>
            </React.Fragment>
          ))}
          <RemainingProfilesExpander remainingProfileIds={remainingProfiles} />
          {reasoning && <ReasoningExpander reasoning={reasoning} />}
        </Column>
      </Column>
    </Column>
  );
}

export default WasteProfileCard;
